import makeFlowId from '@/utils/approval/makeFlowId';
import { pickI18nText, type Lang } from '@/utils/approval/i18nText';
import sortApproversByOrder from '@/utils/approval/sortApproversByOrder';
import type {
  FlowEntry,
  FlowMemberData,
} from '@/components/Approval/ApprovalCreate/ApprovalLineSection';
import type { ApprovalDetailApprover } from '@/types/approval';

export type DetailFlowHydrate = {
  flow: FlowEntry[];
  orderedCooperation: boolean;
};

/**
 * v2 GET 응답의 approvers[]
 *
 * 규칙:
 * - order 오름차순 정렬.
 * - type=1 → 일반 결재자.
 * - type=2 → 협조 결재자 블럭. 협조 블록의 위치는 type=2 멤버 중 가장 작은 order 직전(=order 가 더 작은 type=1 결재자 다음, 더 큰 결재자 앞).
 * - orderedCooperation:
 *   - type=2 멤버가 0~1 명이면 데이터로 판별 불가 — 작성 기본값(false) 적용.
 *   - 전부 동일 order → false (병렬), order 가 갈리면 true.
 */
const approvalDetailToFlow = (
  approvers: ApprovalDetailApprover[],
  lang: Lang,
): DetailFlowHydrate => {
  if (!approvers || approvers.length === 0) {
    return { flow: [], orderedCooperation: false };
  }

  const sorted = sortApproversByOrder(approvers);
  const normalApprovers = sorted.filter((a) => a.type === 1);
  const coopApprovers = sorted.filter((a) => a.type === 2);

  const toMemberData = (
    a: ApprovalDetailApprover,
    prefix: string,
  ): FlowMemberData => ({
    id: makeFlowId(prefix),
    userId: a.passportId,
    kind: 'approver',
    name: pickI18nText(a.name, lang),
    positionName: pickI18nText(a.positionName, lang),
    divisionName: pickI18nText(a.divisionName, lang),
    departmentName: a.departmentName,
    profileImageUrl: a.profileImageUrl,
  });

  const coopOrders = coopApprovers.map((c) => c.order);
  const allSameCoopOrder =
    coopOrders.length > 1 && coopOrders.every((o) => o === coopOrders[0]);
  const orderedCooperation =
    coopApprovers.length <= 1 ? false : !allSameCoopOrder;

  // 협조 블록 삽입 위치: 가장 작은 coop order 직전.
  // type=1 결재자 중 order < minCoopOrder 인 것의 개수.
  const minCoopOrder =
    coopApprovers.length > 0 ? coopApprovers[0].order : Infinity;
  const insertIndex = normalApprovers.filter(
    (a) => a.order < minCoopOrder,
  ).length;

  const flow: FlowEntry[] = [];
  normalApprovers.forEach((a, i) => {
    if (i === insertIndex && coopApprovers.length > 0) {
      flow.push({
        type: 'cooperation',
        id: makeFlowId('coop-edit'),
        members: coopApprovers.map((c) => toMemberData(c, 'coop-edit-m')),
      });
    }
    flow.push({ type: 'member', data: toMemberData(a, 'apv-edit') });
  });
  // 협조가 끝에 와야 하는 경우 (insertIndex 가 normalApprovers.length 이상)
  if (coopApprovers.length > 0 && insertIndex >= normalApprovers.length) {
    flow.push({
      type: 'cooperation',
      id: makeFlowId('coop-edit'),
      members: coopApprovers.map((c) => toMemberData(c, 'coop-edit-m')),
    });
  }

  return { flow, orderedCooperation };
};

export default approvalDetailToFlow;
